import React, { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';

import { Box, Typography, Backdrop, CircularProgress, Button, Grid2 } from '@mui/material';
import { useParams } from 'react-router';
import ClientInfoExtended from './ClientInfoExtended';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ClientForm from './forms/ClientForm';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { RootState } from '../../redux/reducers';
import ProjectsTable from './ProjectsTable';
import ProjectForm from '../projects/form/ProjectForm';
import { importClientForClientDetails, importProjectsForClientDetails } from '../../redux/actions/clientActions';
import { DetailsContainerBox, DetailsInnerContainerBox, DetailsTableContainerBox } from '../../common/ResponsiveStyle';
import { useLocation } from 'react-router-dom';
import { isCustomerPage, isSupplierPage } from './ClientsHelper';
import { ClientType, GoodsStatus } from '../../api/ClientApi';
import { ClientTypeSR } from '../../common/Types';
import {
    canManipulateClients,
    canManipulateProjects,
    canManipulateSuppliers,
} from '../../redux/selectors/employeeSelector';

export default function ClientDetails(): ReactElement {
    const [openUpdateClientForm, setOpenUpdateClientForm] = useState<boolean>(false);
    const [openAddProjectForm, setOpenAddProjectForm] = useState<boolean>(false);

    const client = useAppSelector((state: RootState) => state.clientDetails.client);
    const projects = useAppSelector((state: RootState) => state.clientDetails.projects);

    const canChangeClients = useAppSelector(canManipulateClients);
    const canChangeSuppliers = useAppSelector(canManipulateSuppliers);
    const canAddProject = useAppSelector(canManipulateProjects);

    const dispatch = useAppDispatch();

    const params = useParams();

    const location = useLocation();
    const path = location.pathname;

    const pageClientType = useMemo(() => {
        return isSupplierPage(path) ? ClientType.Supplier : isCustomerPage(path) ? ClientType.Customer : undefined;
    }, [path]);

    useEffect(() => {
        const clientId = params.clientId;

        if (!!clientId && !isNaN(+clientId) && +clientId > 0) {
            dispatch(importClientForClientDetails(+clientId));
        }
    }, [params]);

    useEffect(() => {
        const clientId = params.clientId;

        if (!!clientId && !isNaN(+clientId) && +clientId > 0 && pageClientType === ClientType.Customer) {
            dispatch(importProjectsForClientDetails(+clientId));
        }
    }, [params, pageClientType]);

    const totalValueOnClient = useMemo(() => {
        if (!!projects && projects.length > 0) {
            return projects.reduce((acc, curr) => {
                if (!!curr.sites) {
                    return (acc += curr.sites.reduce((acc1, curr1) => {
                        if (!!curr1.goods) {
                            return (acc1 += curr1.goods
                                .filter((g) => g.goodsStatus === GoodsStatus.InStock)
                                .reduce((acc2, curr2) => {
                                    return (acc2 +=
                                        (curr2.quantity ?? 0) * (curr2.batch?.purchasePricePerArticle ?? 0));
                                }, 0));
                        }
                        return 0;
                    }, 0));
                } else {
                    return 0;
                }
            }, 0);
        }
    }, [projects]);

    const handleOpenClientForm = () => {
        setOpenUpdateClientForm(true);
    };

    const handleCloseClientForm = () => {
        setOpenUpdateClientForm(false);
    };

    const handleOpenProjectForm = () => {
        setOpenAddProjectForm(true);
    };

    const handleCloseProjectForm = () => {
        setOpenAddProjectForm(false);
    };

    return (
        <DetailsContainerBox
            sx={{
                p: 2,
                backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'white' : theme.palette.grey[900]),
            }}
        >
            <Grid2
                container
                sx={{
                    py: 2,
                    px: 4,
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Grid2>
                    <Typography variant="h5" component="h6">
                        {pageClientType ? ClientTypeSR[pageClientType] : ''} - detalji
                    </Typography>
                </Grid2>
                <Grid2 container sx={{ gap: 1 }}>
                    {!!client &&
                        ((pageClientType === ClientType.Customer && canChangeClients) ||
                            (pageClientType === ClientType.Supplier && canChangeSuppliers)) && (
                            <Grid2>
                                <Button
                                    color="warning"
                                    variant="contained"
                                    onClick={handleOpenClientForm}
                                    startIcon={<EditIcon />}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Izmeni {pageClientType ? ClientTypeSR[pageClientType].toLowerCase() + 'a' : ''}
                                </Button>
                            </Grid2>
                        )}
                    {pageClientType === ClientType.Customer && canAddProject && (
                        <Grid2>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={handleOpenProjectForm}
                                startIcon={<AddIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                Dodaj projekat
                            </Button>
                        </Grid2>
                    )}
                </Grid2>
            </Grid2>
            <Box sx={{ '& > :not(style)': { m: 1 } }}>
                <DetailsInnerContainerBox>
                    {!client ? (
                        <Fragment>
                            <Backdrop
                                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                                open={!Boolean(client)}
                            >
                                <CircularProgress color="inherit" />
                            </Backdrop>
                        </Fragment>
                    ) : (
                        <Box>
                            <ClientInfoExtended client={client} clientTotalValue={totalValueOnClient} />
                            {pageClientType === ClientType.Customer && (
                                <DetailsTableContainerBox>
                                    <ProjectsTable projects={projects ?? null} projectFilter="" siteFilter="" />
                                </DetailsTableContainerBox>
                            )}
                        </Box>
                    )}
                </DetailsInnerContainerBox>
            </Box>
            <ClientForm open={openUpdateClientForm} closeForm={handleCloseClientForm} clientId={client?.id} />
            <ProjectForm open={openAddProjectForm} closeForm={handleCloseProjectForm} />
        </DetailsContainerBox>
    );
}
